import { Button } from '@vadiun/react-components';
import { Query, useVerifyAction } from '@vadiun/react-hooks-legacy';
import { ApplicantModel } from 'app/client/clientBusiness/Applicant/models/ApplicantModel';
import { AuthorizedModel } from 'app/client/clientBusiness/Authorized/models/Authorized';
import { PassengerModel } from 'app/client/clientBusiness/Passenger/models/Passenger';
import {
  ClientBusinessModel,
  ClientBusinessPartialModel
} from 'app/client/clientBusiness/models/ClientBusiness';
// import { RateModel } from 'app/client/clientBusiness/rates/models/RateModel';
import {
  ViajeCanalesDeIngreso,
  ViajeTrasladoTipoServicio
} from 'app/viaje/models';
import { ErrorMessage, FieldArray, Form, Formik, FormikProps } from 'formik';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TransferReservationBaseForm } from '../base/TransferReservationBaseForm';
import {
  TransferReservationBatchFormType,
  TransferReservationBatchTripFormType
} from './TransferReservationBatchFormType';
import { transferReservationBatchFormValidation } from './TransferReservationBatchFormValidation';
import { TransferReservationBatchTripForm } from './TransferReservationBatchTripForm';
import {
  createTransferReservationBatchInitialValues,
  createTransferReservationBatchTripInitialValues
} from './TransferreservationBatchFormInitialValues';
import { ReserveDraft } from 'app/viaje/viajeTraslado/borradores/types/TravelDraft';
import { BusType } from '../../models/TransferReservation';
import { useSnackbar } from 'notistack';
import { TravelEstimationResult } from 'app/viaje/services/distanceAndPriceViajeTrasladoCalculator';
import useEventoService from 'app/eventos/services/useEventoService';
import { EventModel } from 'app/eventos/models/Evento';


interface Props {
  initialValues?: TransferReservationBatchFormType;
  onSubmit: (viajeFormValues: TransferReservationBatchFormType, reset) => void;
  getClientesByName: (name: string) => Promise<ClientBusinessPartialModel[]>;
  getClienteById: (id: number) => Promise<ClientBusinessModel | undefined>;
  onClienteSelect: (cliente: ClientBusinessPartialModel) => void;
  solicitantes: ApplicantModel[];
  autorizadosQuery: Query<AuthorizedModel[]>;
  pasajeros: PassengerModel[];
  clientBusiness: ClientBusinessModel | undefined;
  eventsQuery: Query<EventModel[]>;
  fixedRates: any;
  // onServiceTypeSelect: (type: ViajeTrasladoTipoServicio) => void;
  onCalculateDistanceAndCost: (
    viajeFormValues: TransferReservationBatchFormType
  ) => Promise<TravelEstimationResult[]>;
  isDraftPage?: boolean;
  optionsBuses?: BusType[];
  selectedEventId?: number;
  selectedClientId?: number;
}

export default function ReservaTrasladoBatchForm({
  initialValues: _initialValues,
  onSubmit,
  getClientesByName,
  solicitantes,
  pasajeros,
  autorizadosQuery,
  onClienteSelect,
  getClienteById,
  clientBusiness,
  eventsQuery,
  // onServiceTypeSelect,
  onCalculateDistanceAndCost,
  fixedRates,
  isDraftPage,
  optionsBuses,
  selectedEventId,
  selectedClientId
}: Props) {
  const formRef = useRef<FormikProps<TransferReservationBatchFormType>>(null);
  const verifyAction = useVerifyAction();
  const initialValues = useMemo(
    () => _initialValues ?? createTransferReservationBatchInitialValues(),
    [_initialValues]
  );

  const { getEventActives, getEventById } = useEventoService();


  useEffect(() => {
    const fetchSelectedEvent = async () => {
      if (!selectedEventId || !formRef.current) return;

      try {
        const evento = await getEventById(selectedEventId);
        if (evento) {
          formRef.current.setFieldValue('event', evento);
        }
      } catch (err) {
        console.error('Error obteniendo el evento por ID:', err);
      }
    };

    fetchSelectedEvent();
  }, [selectedEventId, getEventById]);



  useEffect(() => {
    const fetchSelectedClient = async () => {
      if (!selectedClientId || !formRef.current) return;

      try {
        const cliente = await getClienteById(selectedClientId);
        if (cliente) {
          formRef.current.setFieldValue('cliente', cliente);
        }
      } catch (err) {
        console.error('Error obteniendo el cliente por ID:', err);
      }
    };

    fetchSelectedClient();
  }, [selectedClientId, getClienteById]);

  const [draftSelected, setDraftSelected] = useState<ReserveDraft>();

  useEffect(() => {
    const draftTravel = localStorage.getItem('travel-selected-draft');
    setDraftSelected(draftTravel ? JSON.parse(draftTravel) : undefined);
  }, []);

  useEffect(() => {
    if (clientBusiness === undefined) return;
    formRef.current?.setFieldValue('prioridad', clientBusiness.hasPriority);
    formRef.current?.setFieldValue('autorizado', undefined);
  }, [clientBusiness]);

  const checkPreviusDate = (viajes) => {
    return viajes.some((viaje: TransferReservationBatchTripFormType) =>
      moment(viaje.fechaYHoraDeLlegada).isBefore(moment())
    );
  };

  const checkMinutesBeforeDeparture = (viajes) => {
    return viajes.some((viaje: TransferReservationBatchTripFormType) =>
      moment(viaje.fechaYHoraDeLlegada).isBefore(
        moment(viaje.minutosPreviosASalida)
      )
    );
  };

  const authorizedDraftTravel = !draftSelected?.authorized_id?.have_error
    ? autorizadosQuery.data?.find(
      (autorizado) =>
        autorizado.name.toLowerCase() ===
        draftSelected?.authorized_id?.data.toLowerCase()
    )
    : undefined;

  const authorizedDefault = authorizedDraftTravel?.id
    ? {
      id: authorizedDraftTravel?.id,
      name: authorizedDraftTravel?.name
    }
    : null;

  const { enqueueSnackbar } = useSnackbar();

  const draftClientBussiness = !draftSelected?.sub_cost_center?.have_error
    ? clientBusiness?.business_client_sub_cost_centers?.find(
      (client) =>
        client.name ===
        (draftSelected?.sub_cost_center
          ? draftSelected.sub_cost_center.data
          : '')
    )
    : undefined;

  const draftClientBussinessSelected = draftClientBussiness?.id
    ? {
      id: draftClientBussiness?.id,
      name: draftClientBussiness?.name
    }
    : null;

  if (draftSelected?.authorized_id?.have_error) {
    formRef.current?.setFieldError(
      'autorizado',
      `Nombre del autorizado: ${draftSelected?.authorized_id?.data}`
    );
  }

  if (draftSelected?.sub_cost_center?.have_error) {
    formRef.current?.setFieldError(
      'subCostCenter',
      `Sub Centro de costo: ${draftSelected?.sub_cost_center?.data}`
    );
  }

  return (
    <Formik<TransferReservationBatchFormType>
      innerRef={formRef}
      initialValues={
        isDraftPage
          ? ({
            autorizado: authorizedDefault,
            buyOrder: '',
            canalIngreso: ViajeCanalesDeIngreso.WHATSAPP,
            cliente: {
              id: draftSelected?.clientable_id,
              fantasyName: draftSelected?.client
            },
            subCostCenter: draftClientBussinessSelected?.name ?? '',
            costCenter: draftSelected?.cost_center?.have_error
              ? ''
              : draftSelected?.cost_center?.data,
            data1: draftSelected?.data_1?.data ?? '',
            data2: draftSelected?.data_2?.data ?? '',
            data3: draftSelected?.data_3?.data ?? '',
            employeeNumber: draftSelected?.employee_number?.have_error
              ? ''
              : draftSelected?.employee_number?.data,
            fixedRate: null,

            isEvent: false,
            isLLTPais: false,
            observacionCoordinacion:
              draftSelected?.coordination_observation?.data ?? '',
            pay_with_cash: false,

            platformApplication: '',
            sector: draftSelected?.sector?.have_error
              ? ''
              : draftSelected?.sector?.data,
            solicitante: {
              name: draftSelected?.applicant_name ?? '',
              phone: draftSelected?.applicant_phone ?? '',
              email: ''
            },
            tripNumber: '',
            viajes: [
              {
                tipoServicio: draftSelected?.service_type?.data ?? '',
                bus_type_id: undefined,
                bus_customer_price: undefined,
                bus_provider_price: undefined,
                bus_is_outsourced: false,
                bus_observation: undefined,
                hasOutSite: false,
                prioridad: false,
                minutosPreviosASalida:
                  moment(
                    `${draftSelected?.date?.data} ${draftSelected?.hour?.have_error
                      ? '00:00'
                      : draftSelected?.hour?.data
                    }`,
                    'DD-MM-YYYY HH:mm'
                  )
                    .subtract(
                      parseInt(
                        draftSelected?.advance_hours?.data.split(':')[0] ??
                        '0'
                      ),
                      'hour'
                    )
                    .subtract(
                      parseInt(
                        draftSelected?.advance_hours?.data.split(':')[1] ??
                        '0'
                      ),
                      'minutes'
                    ) ?? moment(),
                fechaYHoraDeLlegada:
                  moment(
                    `${draftSelected?.date?.data} ${draftSelected?.hour?.have_error
                      ? '00:00'
                      : draftSelected?.hour?.data
                    }`,
                    'DD-MM-YYYY HH:mm'
                  ) ?? moment(),
                puntos:
                  draftSelected?.locations
                    ?.filter((loc) => loc.address.latitude)
                    .map(({ address: { ...rest }, passengers }, index) => {
                      const {
                        latitude,
                        longitude,
                        province,
                        street,
                        street_number,
                        locality
                      } = rest;

                      if (!latitude) {
                        return [];
                      }

                      return {
                        pasajeros: passengers.map(
                          ({ name, phone, email }) => ({
                            nombreCompleto: name,
                            telefono: phone,
                            mail: email
                          })
                        ),
                        domicilio: {
                          latitud: latitude,
                          longitud: longitude,
                          calle: street,
                          numero: street_number,
                          partido: locality,
                          localidad: locality,
                          provincia: province,
                          departamento: '',
                          piso: ''
                        },
                        espera: '0',
                        observacion:
                          index === 0
                            ? draftSelected?.direction_one_observation
                              ?.data ?? ''
                            : index === 1
                              ? draftSelected?.direction_two_observation
                                ?.data ?? ''
                              : draftSelected?.direction_three_observation
                                ?.data ?? ''
                      };
                    }) ?? []
              }
            ],
            plataformaId: null,
            nameEvent: undefined,
          } as any)
          : initialValues
      }
      validationSchema={transferReservationBatchFormValidation(
        clientBusiness,
        formRef.current?.values.isLLTPais ?? false,
        true
      )}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {(formik) => {
        return (
          <Form className="w-full">
            <TransferReservationBaseForm
              {...formik}
              getClientesByName={getClientesByName}
              solicitantes={solicitantes}
              autorizadosQuery={autorizadosQuery}
              onClienteSelect={onClienteSelect}
              clientBusiness={clientBusiness}
              // onServiceTypeSelect={onServiceTypeSelect}
              // fixedRates={fixedRates}
              optionsBuses={optionsBuses}
              eventsQuery={eventsQuery}
              selectedEventId={selectedEventId}
            />
            <FieldArray
              name={'viajes'}
              render={(arrayHelpers) => (
                <>
                  {formik.values.viajes.map((viaje, index) => (
                    <div
                      className="my-4 border-b-2 border-dashed py-8"
                      key={index}
                    >
                      <TransferReservationBatchTripForm
                        autorizadosQuery={autorizadosQuery}
                        clientBusiness={clientBusiness}
                        fixedRates={fixedRates}
                        optionsBuses={optionsBuses}
                        viaje={viaje}
                        index={index}
                        formik={formik}
                        pasajeros={pasajeros}
                        clonar={() => {
                          arrayHelpers.push({
                            ...formik.values.viajes[index]
                          });
                        }}
                        revertir={() =>
                          arrayHelpers.replace(index, {
                            ...formik.values.viajes[index],
                            puntos: formik.values.viajes[index].puntos.reverse()
                          })
                        }
                        borrar={() =>
                          verifyAction({
                            onAccept: () => arrayHelpers.remove(index),
                            body: '¿Está seguro que desea borrar este viaje?'
                          })
                        }
                        previousMinutesError={checkMinutesBeforeDeparture(
                          formik.values.viajes
                        )}
                      />
                    </div>
                  ))}

                  <Button
                    onClick={() => {
                      arrayHelpers.push(
                        createTransferReservationBatchTripInitialValues()
                      );
                    }}
                    variant="light"
                    color="green"
                  >
                    Nuevo Viaje
                  </Button>
                </>
              )}
            />
            <ErrorMessage name="detallesTraslado">
              {(msg) =>
                typeof msg === 'string' ? (
                  <p className="col-span-6 text-sm text-red-500">{msg}</p>
                ) : null
              }
            </ErrorMessage>

            <div className="mt-8 flex justify-between">
              <Button
                variant="contained"
                color="primary"
                disabled={
                  formik.values.viajes.some(
                    (viaje) => viaje?.puntos?.length < 2
                  ) ||
                  formik.values.cliente === null ||
                  formik.values.viajes.every(
                    (viaje) => viaje?.tipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES
                  )
                }
                onClick={() => onCalculateDistanceAndCost(formik.values)}
              >
                Calcular costos y distancias
              </Button>

              <Button
                variant="contained"
                color="green"
                className="ml-auto"
                onClick={() => {
                  const viajesEnPasado = formik.values.viajes.filter((viaje) =>
                    moment(viaje.fechaYHoraDeLlegada).isBefore(
                      moment().subtract(15, 'minutes')
                    )
                  );

                  const motivosIncompletos = viajesEnPasado.some(
                    (viaje) => !viaje.reason_reserve_past?.trim()
                  );

                  if (motivosIncompletos) {
                    enqueueSnackbar(
                      'Debés completar el motivo para poder crear un viaje en el pasado.',
                      {
                        variant: 'error'
                      }
                    );
                    return;
                  }

                  formik.submitForm();
                }}
              >
                Guardar Viaje
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
